export type AllowedFileType = {
  readonly mimeType: string;
  readonly extension: string;
};

export type FileType = {
  readonly type: string;
  readonly extension: string;
};

export const mimeTypeAliases: Readonly<Record<string, string>> = {
  'image/pjpeg': 'image/jpeg',
};

export const normalizeMimeType = (mimeType: string): string => {
  const normalized = mimeType.trim().toLowerCase();
  return mimeTypeAliases[normalized] ?? normalized;
};

export const allowedFileTypes: readonly AllowedFileType[] = [
  { mimeType: 'text/plain', extension: 'txt' },
  {
    mimeType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    extension: 'docx',
  },
  { mimeType: 'application/msword', extension: 'doc' },
  { mimeType: 'text/csv', extension: 'csv' },
  { mimeType: 'application/pdf', extension: 'pdf' },
  { mimeType: 'message/rfc822', extension: 'eml' },
  { mimeType: 'application/vnd.ms-outlook', extension: 'msg' },
  { mimeType: 'application/vnd.ms-excel', extension: 'xls' },
  {
    mimeType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'xlsx',
  },
  {
    mimeType: 'application/vnd.ms-excel.sheet.macroenabled.12',
    extension: 'xlsm',
  },
  { mimeType: 'application/vnd.ms-powerpoint', extension: 'ppt' },
  {
    mimeType:
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    extension: 'pptx',
  },
  { mimeType: 'image/png', extension: 'png' },
  { mimeType: 'image/bmp', extension: 'bmp' },
  { mimeType: 'image/gif', extension: 'gif' },
  { mimeType: 'image/jpeg', extension: 'jpg' },
  { mimeType: 'image/pjpeg', extension: 'jpeg' },
  { mimeType: 'video/x-ms-wmv', extension: 'wmv' },
  { mimeType: 'video/mpeg', extension: 'mpeg' },
  { mimeType: 'video/mp4', extension: 'mp4' },
  { mimeType: 'video/ogg', extension: 'ogg' },
  { mimeType: 'video/x-msvideo', extension: 'avi' },
  { mimeType: 'video/webm', extension: 'webm' },
  { mimeType: 'video/quicktime', extension: 'mov' },
] as const;

export const blockedMimeTypes = new Set<string>([
  'application/x-msdownload',
  'application/x-dosexec',
  'application/x-msdos-program',
  'application/x-sh',
  'application/x-bat',
  'application/x-msi',
  'application/java-archive',
  'application/x-executable',
  'application/x-elf',
  'application/x-mach-binary',
  'application/javascript',
  'text/javascript',
]);

export const blockedExtensions = new Set<string>([
  'exe',
  'dll',
  'com',
  'scr',
  'bat',
  'cmd',
  'msi',
  'sh',
  'bash',
  'zsh',
  'jar',
  'js',
  'jse',
  'vbs',
  'ps1',
  'app',
  'dmg',
]);

export const allowedExtensions = new Set<string>(
  allowedFileTypes.map((fileType) => fileType.extension.toLowerCase()),
);

export const allowedMimeTypes = new Set<string>(
  allowedFileTypes.map((fileType) => fileType.mimeType.toLowerCase()),
);

export const fileTypes: FileType[] = allowedFileTypes.map(
  ({ mimeType, extension }) => ({ type: mimeType, extension }),
);

export const imageTypes: FileType[] = fileTypes.filter(({ type }) =>
  type.startsWith('image/'),
);

export const videoTypes: FileType[] = fileTypes.filter(({ type }) =>
  ['video/mp4', 'video/ogg', 'video/webm'].includes(type),
);

export const pdfTypes: FileType[] = fileTypes.filter(
  ({ type }) => type === 'application/pdf',
);

/**
 * Get the file extension for a given MIME type.
 * Returns the first known extension for the MIME type, or undefined if not found.
 * Useful for generating filenames with correct extensions from MIME types (e.g., Data URLs).
 */
export const getExtensionForMimeType = (
  mimeType: string,
): string | undefined => {
  const normalized = normalizeMimeType(mimeType);
  const fileType = allowedFileTypes.find(
    (ft) => normalizeMimeType(ft.mimeType) === normalized,
  );
  return fileType?.extension.toLowerCase();
};
